import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import { AppConfig } from "src/app/services/app.config.services";
import { EventFilterService } from "src/app/services/event-filter.service";
import { TrsEventsService } from "src/app/services/trsevents.service";
import { TreeItem, TreeviewConfig, TreeviewHelper, TreeviewItem } from 'ngx-treeview';
import { AgentInfoWithProfilePic, IHierarchy, ISupervisorInit } from "src/app/interfaces/agent.interface";

type FilterType = 'teams' | 'status' | 'events' | 'screenblocked-agents';
interface ISelectedEventNames {
    Name: string;
    IsSelected: boolean;
}
interface ISelectedStatusNames {
    IsSelected: boolean;
    Name: string;
}
interface ISelectedTeamNames {
    IsSelected: boolean;
    AgentTeamName: string;
    AgentTeamId: number;
    count: number;
}

@Component({
    selector: 'unified-global-filters',
    templateUrl: './unified-global-filters.html',
    styleUrls: ['./unified-global-filters.sass'],
    host: {
        '(document:click)': 'onClick($event)',
    },
})
export class FilterComponent implements OnInit {
    @Input() isScreenblockFilterEnabled = false;
    @Output() ClosePopUpEvent: EventEmitter<any> = new EventEmitter();
    @ViewChild('FilterView', { read: ElementRef }) public FilterView: ElementRef<any>;

    applyLabel: string = "";
    resetLabel: string = "";
    teamsLabel: string = "";
    statusLabel: string = ""
    eventsLabel: string = "";
    screenblockDisclaimerLabel: string = "";

    _isPopupHidden = true;
    currentlyAppliedFilter: FilterType | 'none' = 'none';
    currentlySelectedFilter: FilterType = 'teams';

    selectedEventNames: { [key: number]: ISelectedEventNames } = [];
    eventNames: TreeviewItem[] = [];

    selectedStatusNames: { [key: number]: ISelectedStatusNames } = [];
    statusNames: TreeviewItem[] = [];

    selectedTeamNames: { [key: number]: ISelectedTeamNames } = [];
    supervisorOrgHierarchy: TreeviewItem[] = [];

    treeViewConfig: TreeviewConfig = {
        hasAllCheckBox: true,
        hasFilter: true,
        hasCollapseExpand: false,
        decoupleChildFromParent: false,
        maxHeight: 300,
        hasDivider: false,
    }

    eventsAndStatusTreeViewConfig: TreeviewConfig = {
        hasAllCheckBox: true,
        hasFilter: true,
        hasCollapseExpand: false,
        decoupleChildFromParent: true,
        maxHeight: 300,
        hasDivider: false,
    }

    constructor(private wsService: TrsEventsService, private translationService: TranslocoService, public eventFilterService: EventFilterService) {
        this.updateComponentLabels();
    }

    ngOnInit(): void {
        this.wsService.evtDataObservable.subscribe(response => {
            switch (response.eventName) {
                case "UPDATE_EVENT_FILTER":
                    this.updateEventFilter();
                    break;
                case "UPDATE_APP_LABELS":
                    this.updateComponentLabels();
                    this.initializeStatusFilter();
                    this.resetEventsFilter();
                    break;
                case "RESET_STATUS_FILTER":
                    this.initializeStatusFilter();
                    break;
                case "GETAGENTSESSIONLIST":
                    this.updateTeamFilter(response.data);
                    break;
                case "UPDATE_TEAM_FOR_AGENT":
                    this.updateTeamForAgent(response.data);
                    break;
                case "SUPERVISORINIT":
                    this.updateOrgHierarchy(response.data);
                    break;
                case "RESET_EVENT_FILTER":
                    this.resetEventsFilter();
                    break;
                case "RESET_TEAMS":
                    this.resetTeamsFilter();
                    break;
                case "REFRESH":
                    this.initializeStatusFilter();
                    this.resetEventsFilter();
                    this.resetTeamsFilter();
                    this.currentlySelectedFilter = 'teams';
                    this.currentlyAppliedFilter = 'none';
                    break;
            }
        });

        this.initializeStatusFilter();
    }

    @Input()
    set isPopupHidden(value: boolean) {
        const wasHidden = this._isPopupHidden;
        this._isPopupHidden = value;

        if (wasHidden && !value)
            this.onPopupOpened();
    }

    get isPopupHidden() {
        return this._isPopupHidden;
    }

    updateComponentLabels = (): void => {
        this.applyLabel = this.translationService.translate("Apply");
        this.resetLabel = this.translationService.translate("Reset");
        this.eventsLabel = this.translationService.translate("Events");
        this.statusLabel = this.translationService.translate("Status");
        this.teamsLabel = this.translationService.translate("Teams");
        this.screenblockDisclaimerLabel = this.translationService.translate("This filter has no options. Clicking on Apply will toggle this filter.");
    }

    onClick(event: { target: any; }) {
        if (!this.FilterView)
            return;

        if (!this.FilterView.nativeElement.contains(event.target)) {
            if (event.target.id != null && (event.target.id == '___unified_global_filter_btn' || event.target.id == '___unified_global_filter_btn_icon'))
                return;
            else this.ClosePopUpEvent.emit();
        }
    }

    closePopup = (): void => this.ClosePopUpEvent.emit();

    setCurrentlySelectedFilter = (filterType: FilterType): void => {
        this.currentlySelectedFilter = filterType;
    }

    initializeStatusFilter = (): void => {
        if (AppConfig) {
            this.selectedStatusNames = {};
            this.statusNames = [];
            let i = 0;
            AppConfig.settings.Status.forEach(element => {
                this.selectedStatusNames[i++] = {
                    IsSelected: true,
                    Name: element,
                };
                this.statusNames.push(new TreeviewItem({
                    text: this.translationService.translate(element),
                    value: element,
                }));
            });
        }
    }

    statusCheckedEvent = ($event: string[]): void => {
        if ($event.length === 0)
            for (let status in this.selectedStatusNames)
                this.selectedStatusNames[status].IsSelected = false;
        else
            for (let status in this.selectedStatusNames) {
                if ($event.includes(this.selectedStatusNames[status].Name))
                    this.selectedStatusNames[status].IsSelected = true;
                else this.selectedStatusNames[status].IsSelected = false;
            }
    }

    updateEventFilter = (): void => {
        const uniqueViolations: Set<string> = this.eventFilterService.getUniqueViolations();
        if (uniqueViolations.size > 0 && (Object.keys(this.selectedEventNames).length !== uniqueViolations.size)) {
            let tempSet: Set<string> = new Set();
            Object.values(this.selectedEventNames).forEach((info: ISelectedEventNames) => {
                tempSet.add(info.Name);
            });

            let i = Object.keys(this.selectedEventNames).length;
            uniqueViolations.forEach((eventName: string) => {
                if (tempSet.has(eventName) === false) {
                    this.selectedEventNames[i++] = {
                        Name: eventName,
                        IsSelected: true,
                    }
                    this.eventNames.push(new TreeviewItem({
                        text: eventName,
                        value: eventName,
                        checked: true,
                    }));
                }
            });
        }
    }

    eventCheckedEvent = ($event: string[]): void => {
        if ($event.length === 0)
            for (let event in this.selectedEventNames)
                this.selectedEventNames[event].IsSelected = false;
        else
            for (let event in this.selectedEventNames) {
                if ($event.includes(this.selectedEventNames[event].Name))
                    this.selectedEventNames[event].IsSelected = true;
                else this.selectedEventNames[event].IsSelected = false;
            }
    }

    resetEventsFilter = (): void => {
        this.selectedEventNames = [];

        let i: number = 0;
        const newItems: TreeviewItem[] = [];

        this.eventFilterService.getUniqueViolations().forEach((eventName: string) => {
            this.selectedEventNames[i++] = {
                Name: eventName,
                IsSelected: true
            }
            newItems.push(new TreeviewItem({
                text: eventName,
                value: eventName,
                checked: true,
            }));
        });

        this.eventNames = newItems;

        TreeviewHelper.concatSelection(this.eventNames, this.eventNames, []);
    }

    updateTeamFilter = (data: AgentInfoWithProfilePic[]): void => {
        data.forEach((agent: AgentInfoWithProfilePic) => {
            this.updateTeamForAgent(agent);
        });
    }

    updateTeamForAgent = (agentInfo: AgentInfoWithProfilePic): void => {
        if (agentInfo.IsArchived == false)
            if (this.selectedTeamNames[agentInfo.AgentTeamId] == null) {
                this.selectedTeamNames[agentInfo.AgentTeamId] = {
                    IsSelected: true,
                    AgentTeamName: agentInfo.AgentTeamName,
                    AgentTeamId: agentInfo.AgentTeamId,
                    count: 1
                };
            }
            else this.selectedTeamNames[agentInfo.AgentTeamId].count++;
    }

    teamCheckedEvent = ($event: number[]): void => {
        if ($event.length === 0)
            for (let team in this.selectedTeamNames) {
                this.selectedTeamNames[team].IsSelected = false;
            }
        else {
            for (let team in this.selectedTeamNames) {
                if ($event.includes(this.selectedTeamNames[team].AgentTeamId))
                    this.selectedTeamNames[team].IsSelected = true;
                else this.selectedTeamNames[team].IsSelected = false;
            }
        }
    }

    updateOrgHierarchy = (supervisorInfo: ISupervisorInit): void => {
        this.selectedTeamNames = {};

        this.supervisorOrgHierarchy = [new TreeviewItem({
            text: `${supervisorInfo.TeamsInHierarchy.TeamName} (${supervisorInfo.TeamsInHierarchy.LevelHierarchy})`,
            value: supervisorInfo.TeamsInHierarchy.TeamId,
            children: this.getChildHierarchy(supervisorInfo.TeamsInHierarchy.Subteams)
        })];

        if (supervisorInfo.TeamsInHierarchy.LevelHierarchy === "Team")
            this.selectedTeamNames[supervisorInfo.TeamsInHierarchy.TeamId] = {
                AgentTeamId: supervisorInfo.TeamsInHierarchy.TeamId,
                AgentTeamName: supervisorInfo.TeamsInHierarchy.TeamName,
                count: 0,
                IsSelected: true,
            }
    }

    getChildHierarchy = (subteams: IHierarchy[]): TreeItem[] => {
        let orgHierarchy: TreeItem[] = [];

        subteams.forEach((subteam: IHierarchy) => {
            orgHierarchy.push(new TreeviewItem({
                text: `${subteam.TeamName} (${subteam.LevelHierarchy})`,
                value: subteam.TeamId,
                children: this.getChildHierarchy(subteam.Subteams),
            }));

            if (subteam.LevelHierarchy === "Team")
                this.selectedTeamNames[subteam.TeamId] = {
                    AgentTeamId: subteam.TeamId,
                    AgentTeamName: subteam.TeamName,
                    count: 0,
                    IsSelected: true,
                }
        });

        return orgHierarchy;
    }

    resetTeamsFilter = (): void => {
        this.supervisorOrgHierarchy.forEach((team: TreeviewItem) => {
            team.setCheckedRecursive(true);
        });

        for (let team in this.selectedTeamNames) {
            this.selectedTeamNames[team].IsSelected = true;
        }
    }

    applyGlobalFilter = (): void => {
        let previousLength: number = 0;

        switch (this.currentlySelectedFilter) {
            case 'teams':
                let teamsArr: string[] = [];

                for (let team in this.selectedTeamNames)
                    if (this.selectedTeamNames[team].IsSelected)
                        teamsArr.push(this.selectedTeamNames[team].AgentTeamName.toLowerCase());

                previousLength = teamsArr.length;

                if (teamsArr.length === 0)
                    teamsArr.push(" ");
                else if (teamsArr.length === Object.keys(this.selectedTeamNames).length)
                    teamsArr = [];

                this.sendEvent("FILTER_AGENTS_BY_TEAM", teamsArr);
                this.currentlyAppliedFilter = (previousLength === Object.keys(this.selectedTeamNames).length) ? 'none' : 'teams';
                break;
            case 'status':
                let statusArr = [];

                for (let status in this.selectedStatusNames)
                    if (this.selectedStatusNames[status].IsSelected)
                        statusArr.push(this.selectedStatusNames[status].Name.toLowerCase());

                previousLength = statusArr.length;

                if (statusArr.length === 0)
                    statusArr.push(" ");
                else if (statusArr.length === Object.keys(this.selectedStatusNames).length)
                    statusArr = [];

                this.sendEvent("FILTER_AGENTS_BY_STATUS", statusArr);
                this.currentlyAppliedFilter = (previousLength === Object.keys(this.selectedStatusNames).length) ? 'none' : 'status';
                break;
            case 'events':
                let eventFilterSet: Set<string> = new Set();

                Object.values(this.selectedEventNames).forEach((info: ISelectedEventNames) => {
                    if (info.IsSelected === true)
                        eventFilterSet.add(info.Name);
                });

                previousLength = eventFilterSet.size;

                if (eventFilterSet.size === 0)
                    eventFilterSet.add(" ");
                else if (eventFilterSet.size === Object.keys(this.selectedEventNames).length)
                    eventFilterSet = new Set();

                this.sendEvent("FILTER_AGENTS_BY_EVENTS", eventFilterSet);
                this.currentlyAppliedFilter = (previousLength === Object.keys(this.selectedEventNames).length) ? 'none' : 'events';
                break;
            case 'screenblocked-agents':
                this.currentlyAppliedFilter = 'none';
                this.sendEvent("FILTER_AGENTS_BY_SCREENBLOCK", {});
                break;
        }

        this.ClosePopUpEvent.emit();
    }

    resetGlobalFilters = (): void => {
        this.ClosePopUpEvent.emit();
        this.currentlySelectedFilter = 'teams';
        this.currentlyAppliedFilter = 'none';
        this.sendEvent("CLEAR_ALL_FILTERS", {});
    }

    onPopupOpened = (): void => {
        if (this.currentlyAppliedFilter !== 'none')
            this.currentlySelectedFilter = this.currentlyAppliedFilter;
        else {
            if (this.isScreenblockFilterEnabled) {
                this.currentlySelectedFilter = 'screenblocked-agents';
                this.currentlyAppliedFilter = 'screenblocked-agents';
            }
        }
    }

    sendEvent = (eventName: string, responseData: any): void => this.wsService.evtdata.next({
        "eventName": eventName,
        "data": responseData
    });
}